import { DateTime } from 'luxon'
import type { MetadataType } from '../MetadataFields.js'

/** `groupByValue` uses this for a sample whose field is unset; it belongs after the real values. */
const UNSET = 'undefined'

const alphanumeric = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/**
 * Datetime group values are the raw ISO strings straight off the metadata, and those carry whatever
 * offset the value was stored with — `+02:00` in summer, `+01:00` in winter. Comparing them as text
 * therefore misorders any pair that straddles the change, which is why this parses instead. Unparsable
 * values fall back to text so a malformed date still lands somewhere stable rather than moving about.
 */
const compareDates = (a: string, b: string): number => {
  const left = DateTime.fromISO(a, { zone: 'utc' })
  const right = DateTime.fromISO(b, { zone: 'utc' })
  if (!left.isValid || !right.isValid) {
    return alphanumeric.compare(a, b)
  }
  return left.toMillis() - right.toMillis()
}

/**
 * Orders the buckets a grouping produces: chronologically for a date, and alphanumerically otherwise —
 * numeric-aware, so `Sample 2` precedes `Sample 10`.
 */
export const compareGroupValues = (a: string, b: string, type: MetadataType): number => {
  if (a === UNSET || b === UNSET) {
    return a === b ? 0 : a === UNSET ? 1 : -1
  }
  if (type === 'datetime' || type === 'datetime:duration') {
    return compareDates(a, b)
  }
  return alphanumeric.compare(a, b)
}
